<script lang="ts">
	//Design system components
	import {
		Wizard,
		WizardItem,
		Button,
		Modal,
		Container,
		WizardContainer,
		WizardContent,
		toast,
	} from '@Shelly/shelly-design-system';
	//Components
	import CarPlayDevicesSelection from './WizardSteps/CarPlayDevicesSelection.svelte';
	import CarPlayOrder from './WizardSteps/CarPlayOrder.svelte';
	//Services
	import { format } from 'svelte-i18n';
	import { postReq } from 'src/services/http';
	import { add_car_play_devices } from 'src/services/native_container_requests_ios';
	//Helpers
	import { setWizardSteps, updateWizardStepOnWizardItem } from 'src/components/Lists/list-helpers';
	//Stores
	import { devices } from 'src/stores/lists/devices';
	import { writable } from 'svelte/store';
	import { selected_for_car_play } from 'src/stores/features/car-play';
	//Svelte hooks
	import { onMount } from 'svelte';
	//Types
	import type { CloudApiResponse } from 'src/types/responses/CloudApiResponse';
	import { showNewLayout } from 'src/stores/toDelete/new_layout';

	export let showModal = false;

	let modalRef: Modal = null;

	const steps = [
		{
			title: $format('label_car_play_select_devices', {
				default: 'CarPlay Select Devices',
			}),
			component: CarPlayDevicesSelection,
		},
		{
			title: $format('label_car_play_reordering_devices', {
				default: 'CarPlay reordering devices',
			}),
			component: CarPlayOrder,
		},
	];

	let initial_car_play_list = Array.from($selected_for_car_play); //used to store the initial list of devices for closing the wizard
	let wizardItems = setWizardSteps(steps.map((step) => step.title)); //used to store the wizard items
	let currentStep = wizardItems[0]; //used to store the current wizard step
	let wizardItemsContainerRef: HTMLElement = null; //used to store the wizard items container
	let containersRefsObj = writable({
		//used to store the containers refs
		scrollContainerRef: null,
		wizardContainerRef: null,
	});
	let loading = false;

	$: isLastStepWizard = currentStep.activeIndex === steps.length - 1; //used to check if the current step is the last and update the next button

	$: nextButton = isLastStepWizard
		? $format('button_save', { default: 'Save' })
		: $format('button_next', { default: 'Next' });

	onMount(async () => {
		const res: CloudApiResponse = await postReq('user/get_car_play_devices', {}); //getting the list of devices

		if (res?.data) {
			selected_for_car_play.set(res.data.filter((id) => typeof $devices[id] !== 'undefined'));
			initial_car_play_list = $selected_for_car_play; //updating the initial list
		} else {
			selected_for_car_play.set([]); //resetting the store if there is no data
		}

		//updating selected_for_car_play store on mount;
	});

	const updateWizardItems = (index: number) => {
		const result = updateWizardStepOnWizardItem(index, wizardItems, currentStep);
		wizardItems = result.wizardItems;
		currentStep = { ...result.activeWizardStep };
	};

	const nextStep = () => {
		if (currentStep.activeIndex < steps.length - 1) {
			currentStep.activeIndex += 1;
		}
		updateWizardItems(currentStep.activeIndex); //updating the wizard items based on the current step
	};

	const prevStep = () => {
		if (currentStep.activeIndex > 0) {
			currentStep.activeIndex -= 1;
		}
		updateWizardItems(currentStep.activeIndex); //updating the wizard items based on the current step
	};

	const resetStoreSelection = () => {
		selected_for_car_play.set(initial_car_play_list); //resetting the store used on closing the wizard or on error while saving the devices
	};

	const closeWizard = () => {
		showModal = false;
		currentStep.activeIndex = 0;
		updateWizardItems(currentStep.activeIndex); //reset wizard items and
	};

	const saveCarPlayDevices = async () => {
		loading = true;
		add_car_play_devices($selected_for_car_play); //update native container
		const res = await postReq('user/set_car_play_devices', {
			devices: JSON.stringify($selected_for_car_play),
		}); //update server

		if (res?.isok) {
			toast.open({
				type: 'info',
				title: $format('carplay_selection_success', {
					default: 'CarPlay available devices updated successfully.',
				}),
			});
		} else {
			// reset to initial selection
			resetStoreSelection();
			toast.open({
				type: 'danger',
				title: $format('carplay_selection_error', {
					default: 'Failed to set CarPlay available devices.',
				}),
			});
		}

		closeWizard();
	};
</script>

<Modal
	bind:showModal
	on:close={() => {
		closeWizard();
		resetStoreSelection();
	}}
	title={$format('title_car_play_devices', { default: 'CarPlay Devices' })}
	bind:this={modalRef}
	removeScrollPadding
>
	<Wizard slot="toolbar">
		{#each wizardItems as item, i (i)}
			<WizardItem
				on:click={() => {
					updateWizardItems(i);
				}}
				active={item.active}
				title={item.title}
				showMoreDots={item.showMoreDots}
				indicatorColor={$showNewLayout ? 'color-2' : undefined}
			/>
		{/each}
	</Wizard>
	<Container padding="0" slot="content">
		<WizardContainer
			adaptiveHeight
			fullHeight={currentStep.activeIndex == 1}
			activeStepIndex={currentStep.activeIndex}
			bind:wizardItemsContainerRef
			bind:wizardContainerRef={$containersRefsObj.wizardContainerRef}
		>
			{#each wizardItems as _, i (i)}
				<!-- DO NOT REMOVE COMMENT: Padding references -->
				<WizardContent active={currentStep.activeIndex === i} class="pt-s">
					<svelte:component this={steps[i].component} />
				</WizardContent>
			{/each}
		</WizardContainer>
	</Container>
	<div class="d-flex justify-content-end" slot="footer">
		{#if currentStep.activeIndex !== 0}
			<Button
				class="min-width-md-m min-width-sm-auto width-md-auto width-sm-full text-nowrap mr-s"
				variant="secondary"
				on:click={prevStep}
				icon="fas fa-chevron-left"
			>
				{$format('button_back', { default: 'Back' })}
			</Button>
		{/if}
		<Button
			class="min-width-md-m min-width-sm-auto width-md-auto width-sm-full text-nowrap"
			on:click={isLastStepWizard ? saveCarPlayDevices : nextStep}
			icon={isLastStepWizard ? 'fas fa-save' : false}
			rightIcon={isLastStepWizard ? false : 'fas fa-chevron-right'}
		>
			{nextButton}
		</Button>
	</div>
</Modal>
